import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RiDashboardLine,
  RiUserLine,
  RiMessage2Line,
  RiMoneyDollarCircleLine,
  RiFileEditLine,
  RiLogoutBoxLine,
  RiMenuLine,
  RiCloseLine,
  RiCalendarEventLine,
  RiSettings4Line,
  RiHomeLine,
  RiMailLine,
  RiPhoneLine,
  RiLink
} from 'react-icons/ri';
import { useAuth } from '../context/AuthContext';
import UserManagement from '../admin/UserManagement';
import MessageCenter from '../admin/MessageCenter';
import PaymentTracker from '../admin/PaymentTracker';
import ContentManagement from '../admin/ContentManagement';
import Overview from '../admin/Overview';
import SummitManagement from '../admin/SummitManagement';
import ContactManagement from '../admin/ContactManagement';
import SlugManagement from '../pages/slugmanagement';

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { logout } = useAuth();
  const location = useLocation();

  // Check for mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset scroll position on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const navItems = [
    { path: '/dashboard', icon: RiDashboardLine, label: 'Overview' },
    { path: '/dashboard/users', icon: RiUserLine, label: 'Users' },
    { path: '/dashboard/contact', icon: RiMailLine, label: 'Contact' },
    { path: '/dashboard/messages', icon: RiMessage2Line, label: 'Messages' },
    { path: '/dashboard/payments', icon: RiMoneyDollarCircleLine, label: 'Payments' },
    { path: '/dashboard/slug', icon: RiLink, label: 'Slug' },
    { path: '/dashboard/cms', icon: RiFileEditLine, label: 'CMS' },
    { path: '/dashboard/summits', icon: RiCalendarEventLine, label: 'Summits' },
  ];

  const MobileOverlay = () => (
    <AnimatePresence>
      {sidebarOpen && isMobile && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
        />
      )}
    </AnimatePresence>
  );

  const handleLogout = async () => {
    await logout();
    window.location.href = '/admin';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen overflow-hidden">
        <MobileOverlay />

        {/* Sidebar */}
        <motion.aside
          initial={{ x: -320 }}
          animate={{ x: sidebarOpen ? 0 : -320 }}
          transition={{ type: 'spring', damping: 20 }}
          className={`fixed lg:relative z-40 w-72 h-screen bg-white border-r border-gray-200 flex flex-col ${
            sidebarOpen ? 'block' : 'hidden lg:block'
          }`}
        >
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <RiCalendarEventLine className="text-white text-xl" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">MEETINGS21</h2>
                  <p className="text-xs text-gray-500">Admin Dashboard</p>
                </div>
              </div>
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-gray-500 hover:text-gray-700 rounded-lg p-2 transition"
                >
                  <RiCloseLine size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto py-6 px-4">
            <div className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => isMobile && setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                      isActive
                        ? 'bg-blue-50 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg transition-all ${
                        isActive
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-gray-100 text-gray-500 group-hover:bg-blue-100 group-hover:text-blue-600'
                      }`}
                    >
                      <item.icon size={18} />
                    </div>
                    <span className="font-medium">{item.label}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="ml-auto w-1 h-5 rounded-full bg-blue-600"
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-all duration-200 group font-medium"
            >
              <RiLogoutBoxLine size={18} className="group-hover:scale-110 transition-transform" />
              <span>Logout</span>
            </button>
          </div>
        </motion.aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden text-gray-600 hover:text-gray-900 p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <RiMenuLine size={22} />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Manage your conference dashboard
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs text-gray-600">Live</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 border border-red-200 hover:border-red-300"
              >
                <RiLogoutBoxLine size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              <Routes>
                <Route index element={<Overview />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="contact" element={<ContactManagement />} />
                <Route path="messages" element={<MessageCenter />} />
                <Route path="payments" element={<PaymentTracker />} />
                <Route path="slug" element={<SlugManagement />} />
                <Route path="cms" element={<ContentManagement />} />
                <Route path="summits" element={<SummitManagement />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;